import React, { useState, useEffect } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { authHandler } from "../redux/handlers";
import NotFound from "../pages/NotFound";
import ChangeBusinessSlug from "../pages/ChangeBusinessSlug";

const ProtectedLayout = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { businessSlug } = useParams();
  let currentPath = location.pathname;
  currentPath = currentPath.replace(/\/+/g, "/");

  if (currentPath !== "/" && currentPath.endsWith("/")) {
    currentPath = currentPath.slice(0, -1);
  }

  const [isLoading, setIsLoading] = useState(true);
  const [isShowNotFound, setIsShowNotFound] = useState(false);
  const [showBusinessNotMatching, setShowBusinessNotMatching] = useState(false);

  const authState = {
    accessToken: localStorage.getItem("accessToken"),
    email: localStorage.getItem("email"),
    branchId: localStorage.getItem("branchId"),
    branchPOSId: localStorage.getItem("branchPOSId"),
    branchKitchenId: localStorage.getItem("branchKitchenId"),
    branchPOSSelling: localStorage.getItem("branchPOSSelling"),
    closePrintObj: localStorage.getItem("closePrintObj"),
  };

  const hasAllCredentials =
    authState.accessToken &&
    authState.branchId &&
    authState.email &&
    authState.branchPOSId;
  const hasAccessTokenAndEmail =
    authState.accessToken && authState.email && !authState.branchId;
  const hasPartialCredentials =
    authState.accessToken &&
    authState.branchId &&
    authState.email &&
    !authState.branchPOSId;

  const pathMatchers = {
    isPOSMain: currentPath === `/${businessSlug}`,
    isPOSSetting: currentPath.includes(`/${businessSlug}/pos-setting`),
    isBranchSelection: currentPath.includes(
      `/${businessSlug}/branch-selection`
    ),
    isVerifyPIN: currentPath.includes(`/${businessSlug}/verify-pin`),
    isKitchen: currentPath.includes(`/${businessSlug}/kitchen`),
    isKitchenHistory: currentPath.includes(`/${businessSlug}/kitchen-history`),
    isChecker: currentPath.includes(`/${businessSlug}/checker`),
    isPrintPath: [
      `/${businessSlug}/close-balance-print`,
      `/${businessSlug}/pos-start-print`,
      `/${businessSlug}/recipt-print`,
      `/${businessSlug}/recipt-print-copy`,
      `/${businessSlug}/recipt-print-invoice`,
      `/${businessSlug}/pos-deposit-print`,
      `/${businessSlug}/pos-withdraw-print`,
      `/${businessSlug}/pos-open-store-print`,
    ].some((path) => currentPath.includes(path)),
    isMainFeature: [
      `/${businessSlug}/notification`,
      `/${businessSlug}/table`,
      `/${businessSlug}/bill`,
      `/${businessSlug}/save`,
      `/${businessSlug}/menu`,
      `/${businessSlug}/shop`,
      `/${businessSlug}/money-drawer`,
    ].some((path) => currentPath.includes(path)),
  };

  useEffect(() => {
    const checkBusinessGuardAccess = async () => {
      try {
        const response = await dispatch(
          authHandler.checkBusinessSlug(businessSlug)
        );

        if (response !== "Business slug is exist") {
          setIsShowNotFound(true);
        }
      } catch (error) {
        console.error("Failed to check business slug:", error);
        setIsShowNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };

    const handleBusinessNotMatching = () => setShowBusinessNotMatching(true);
    const handleBusinessNotFound = () => setIsShowNotFound(true);

    if (businessSlug) {
      checkBusinessGuardAccess();
    } else {
      setIsLoading(false);
    }

    window.addEventListener("businessNotMatching", handleBusinessNotMatching);
    window.addEventListener("businessNotFound", handleBusinessNotFound);

    return () => {
      window.removeEventListener(
        "businessNotMatching",
        handleBusinessNotMatching
      );
      window.removeEventListener("businessNotFound", handleBusinessNotFound);
    };
  }, [dispatch, businessSlug]);

  useEffect(() => {
    if (businessSlug) {
      localStorage.setItem("businessSlug", businessSlug);
    }
  }, [businessSlug]);

  if (isLoading) {
    return (
      <div
        style={{ height: "100vh", width: "100vw", backgroundColor: "white" }}
      ></div>
    );
  }

  if (isShowNotFound) {
    return <NotFound />;
  }

  if (showBusinessNotMatching) {
    return <ChangeBusinessSlug />;
  }

  if (authState.branchKitchenId) {
    if (
      pathMatchers.isPOSMain ||
      pathMatchers.isPOSSetting ||
      pathMatchers.isPrintPath ||
      pathMatchers.isMainFeature ||
      pathMatchers.isBranchSelection ||
      pathMatchers.isVerifyPIN
    ) {
      return <Navigate to={`/${businessSlug}/kitchen`} replace />;
    }
  }

  if (authState.branchPOSSelling) {
    if (
      pathMatchers.isKitchen ||
      pathMatchers.isKitchenHistory ||
      pathMatchers.isChecker ||
      pathMatchers.isVerifyPIN ||
      pathMatchers.isBranchSelection
    ) {
      return <Navigate to={`/${businessSlug}`} replace />;
    }
  }

  if (
    hasAccessTokenAndEmail &&
    !pathMatchers.isBranchSelection &&
    !pathMatchers.isVerifyPIN
  ) {
    return <Navigate to={`/${businessSlug}/branch-selection`} replace />;
  }

  if (
    hasPartialCredentials &&
    !pathMatchers.isPOSSetting &&
    !authState.branchKitchenId
  ) {
    return <Navigate to={`/${businessSlug}/pos-setting`} replace />;
  }

  if (
    hasAllCredentials &&
    !authState.branchKitchenId &&
    !pathMatchers.isPOSMain &&
    !pathMatchers.isMainFeature &&
    !pathMatchers.isPrintPath
  ) {
    return <Navigate to={`/${businessSlug}`} replace />;
  }

  if (
    pathMatchers.isPOSMain ||
    pathMatchers.isMainFeature ||
    pathMatchers.isPrintPath
  ) {
    if (!hasAllCredentials && !authState.closePrintObj) {
      return <Navigate to={`/${businessSlug}/login`} replace />;
    }
  } else if (pathMatchers.isPOSSetting) {
    if (!(authState.accessToken && authState.branchId && authState.email)) {
      return <Navigate to={`/${businessSlug}/login`} replace />;
    }

    if (authState.branchPOSId) {
      localStorage.removeItem("branchPOSId");
    }
  } else if (pathMatchers.isBranchSelection || pathMatchers.isVerifyPIN) {
    if (!(authState.accessToken && authState.email)) {
      return <Navigate to={`/${businessSlug}/login`} replace />;
    }

    if (authState.branchId) localStorage.removeItem("branchId");
    if (authState.branchPOSId) localStorage.removeItem("branchPOSId");
  } else if (
    pathMatchers.isKitchen ||
    pathMatchers.isKitchenHistory ||
    pathMatchers.isChecker
  ) {
    if (!authState.branchKitchenId) {
      if (authState.branchPOSId) localStorage.removeItem("branchPOSId");
      return <Navigate to={`/${businessSlug}/pos-setting`} replace />;
    }
  }

  return <Outlet />;
};

export default ProtectedLayout;
